/**
 * Map Validators — Stage 1b
 *
 * Covers: map_locate_region, map_locate_point, map_place_cities.
 *
 * NOTE: map_label_regions is deprecated (0 usage).
 */

import { blocker, coach, STAGE } from './shared';
import type { ValidationIssue } from './shared';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isFiniteNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}
function isValidLatLon(p: unknown): p is {
  lat: number;
  lon: number;
} {
  return !!p && typeof p === 'object' && isFiniteNumber((p as Record<string, unknown>).lat) && isFiniteNumber((p as Record<string, unknown>).lon);
}

// ─── map_locate_region ───────────────────────────────────────────────────────

export function validateMapLocateRegion(data: Record<string, unknown>, qId: string, fId: string): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const correctRegionId = data.correctRegionId;
  if (typeof correctRegionId !== 'string' || correctRegionId.trim() === '') {
    issues.push(blocker('type', STAGE, 'TYPE_MAP_INVALID_REGION_ID', 'map_locate_region: data.correctRegionId must be a non-empty string', {
      field: 'data.correctRegionId',
      actual: String(correctRegionId),
      questionId: qId,
      familyId: fId,
      suggestion: 'Use the canonical region id from the atlas (e.g. "NL-LI" for Limburg)'
    }));
  }
  const showLabels = data.showLabels;
  if (showLabels !== undefined && showLabels !== 'all' && showLabels !== 'highlighted' && showLabels !== 'none') {
    issues.push(coach('type', STAGE, 'TYPE_MAP_INVALID_SHOW_LABELS', `map_locate_region: data.showLabels must be one of "all" | "highlighted" | "none" (got "${String(showLabels)}")`, {
      field: 'data.showLabels',
      actual: String(showLabels),
      expected: '"all" | "highlighted" | "none"',
      questionId: qId,
      familyId: fId
    }));
  }
  return issues;
}

// ─── map_locate_point ────────────────────────────────────────────────────────

export function validateMapLocatePoint(data: Record<string, unknown>, qId: string, fId: string): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const mapId = String(data.mapId ?? '');
  if (!mapId) {
    issues.push(blocker('type', STAGE, 'TYPE_MISSING_DATA_FIELD', 'map_locate_point: data.mapId is required', {
      field: 'data.mapId',
      questionId: qId,
      familyId: fId
    }));
  }
  const mode = data.mode === 'snap-to-dot' ? 'snap-to-dot' : 'free';
  const isSnapToDot = mode === 'snap-to-dot' || data.correctCityId && !data.targetPoint;
  if (isSnapToDot) {
    const cityIds = Array.isArray(data.candidateCityIds) ? data.candidateCityIds : Array.isArray(data.cityIds) ? data.cityIds : [];
    if (cityIds.length === 0) {
      issues.push(blocker('type', STAGE, 'TYPE_MISSING_DATA_FIELD', 'map_locate_point (snap-to-dot): data.candidateCityIds must be a non-empty array of city ids', {
        field: 'data.candidateCityIds',
        questionId: qId,
        familyId: fId
      }));
    } else if (cityIds.length < 3) {
      issues.push(coach('type', STAGE, 'TYPE_SUBOPTIMAL_CONFIG', `map_locate_point (snap-to-dot): data.candidateCityIds has ${cityIds.length} entries — minimum 3 recommended (correct + ≥2 distractors)`, {
        field: 'data.candidateCityIds',
        questionId: qId,
        familyId: fId
      }));
    }
    const correctCityId = String(data.correctCityId ?? '');
    if (!correctCityId) {
      issues.push(blocker('type', STAGE, 'TYPE_MISSING_DATA_FIELD', 'map_locate_point (snap-to-dot): data.correctCityId must be a non-empty string', {
        field: 'data.correctCityId',
        questionId: qId,
        familyId: fId
      }));
    } else if (cityIds.length > 0 && !cityIds.includes(correctCityId)) {
      issues.push(blocker('type', STAGE, 'TYPE_INVALID_DATA', `map_locate_point (snap-to-dot): data.correctCityId "${correctCityId}" is not present in data.candidateCityIds`, {
        field: 'data.correctCityId',
        questionId: qId,
        familyId: fId
      }));
    }
    if (!data.focusRegionId) {
      issues.push(coach('type', STAGE, 'TYPE_SUBOPTIMAL_CONFIG', 'map_locate_point (snap-to-dot): data.focusRegionId is recommended to zoom the map onto the relevant region', {
        field: 'data.focusRegionId',
        questionId: qId,
        familyId: fId
      }));
    }
  } else {
    // Free mode
    const target = data.targetPoint as Record<string, unknown> | undefined;
    if (!target || typeof target.lat !== 'number' || typeof target.lon !== 'number') {
      issues.push(blocker('type', STAGE, 'TYPE_MISSING_DATA_FIELD', 'map_locate_point (free): data.targetPoint must be an object with {lat, lon}', {
        field: 'data.targetPoint',
        questionId: qId,
        familyId: fId
      }));
    }
    if (data.toleranceKm !== undefined && typeof data.toleranceKm !== 'number') {
      issues.push(blocker('type', STAGE, 'TYPE_INVALID_DATA', 'map_locate_point (free): data.toleranceKm must be a number', {
        field: 'data.toleranceKm',
        questionId: qId,
        familyId: fId
      }));
    }
  }

  // Common reference cities validation
  if (data.showOtherCities !== undefined) {
    issues.push(coach('type', STAGE, 'TYPE_DEPRECATED_FIELD', 'map_locate_point: data.showOtherCities is deprecated, use data.referenceCityIds instead', {
      field: 'data.showOtherCities',
      questionId: qId,
      familyId: fId
    }));
  }
  const refCities = data.referenceCityIds ?? data.showOtherCities;
  if (refCities !== undefined && !Array.isArray(refCities)) {
    issues.push(blocker('type', STAGE, 'TYPE_INVALID_DATA', 'map_locate_point: data.referenceCityIds must be an array of strings', {
      field: 'data.referenceCityIds',
      questionId: qId,
      familyId: fId
    }));
  }
  return issues;
}

// ─── map_locate_line ─────────────────────────────────────────────────────────

export function validateMapLocateLine(data: Record<string, unknown>, qId: string, fId: string): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const mapId = String(data.mapId ?? '');
  if (!mapId) {
    issues.push(blocker('type', STAGE, 'TYPE_MISSING_DATA_FIELD', 'map_locate_line: data.mapId is required', {
      field: 'data.mapId',
      questionId: qId,
      familyId: fId
    }));
  }
  const targetRiverId = data.targetRiverId;
  if (typeof targetRiverId !== 'string' || targetRiverId.trim() === '') {
    issues.push(blocker('type', STAGE, 'TYPE_MISSING_DATA_FIELD', 'map_locate_line: data.targetRiverId must be a non-empty string (e.g. "NL-RIJN")', {
      field: 'data.targetRiverId',
      questionId: qId,
      familyId: fId
    }));
  }
  const tol = data.toleranceKm;
  if (tol !== undefined && (typeof tol !== 'number' || tol <= 0)) {
    issues.push(blocker('type', STAGE, 'TYPE_INVALID_DATA', `map_locate_line: data.toleranceKm must be a positive number (got ${String(tol)})`, {
      field: 'data.toleranceKm',
      actual: String(tol),
      expected: '> 0 (typically 8-15 km)',
      questionId: qId,
      familyId: fId
    }));
  } else if (typeof tol === 'number' && (tol < 5 || tol > 25)) {
    issues.push(coach('type', STAGE, 'TYPE_SUBOPTIMAL_CONFIG', `map_locate_line: data.toleranceKm of ${tol} is outside the recommended 5-25 km range`, {
      field: 'data.toleranceKm',
      questionId: qId,
      familyId: fId
    }));
  }
  if (data.referenceCityIds !== undefined && !Array.isArray(data.referenceCityIds)) {
    issues.push(blocker('type', STAGE, 'TYPE_INVALID_DATA', 'map_locate_line: data.referenceCityIds must be an array of strings when present', {
      field: 'data.referenceCityIds',
      questionId: qId,
      familyId: fId
    }));
  }
  return issues;
}

// ─── map_label_regions ───────────────────────────────────────────────────────

export function validateMapLabelRegions(data: Record<string, unknown>, qId: string, fId: string): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const labels = data.labels;
  if (!Array.isArray(labels)) {
    issues.push(blocker('type', STAGE, 'TYPE_MAP_INVALID_LABELS', 'map_label_regions: data.labels must be an array of { id, text, correctRegionId }', {
      field: 'data.labels',
      actual: JSON.stringify(labels),
      questionId: qId,
      familyId: fId
    }));
    return issues;
  }
  if (labels.length < 2) {
    issues.push(blocker('type', STAGE, 'TYPE_MAP_LABELS_TOO_FEW', `map_label_regions: needs at least 2 labels (got ${labels.length})`, {
      field: 'data.labels',
      actual: String(labels.length),
      expected: '≥ 2',
      questionId: qId,
      familyId: fId
    }));
  }
  const seenIds = new Set<string>();
  for (let i = 0; i < labels.length; i++) {
    const entry = labels[i] as Record<string, unknown> | undefined;
    if (!entry || typeof entry !== 'object') {
      issues.push(blocker('type', STAGE, 'TYPE_MAP_LABEL_INVALID_SHAPE', `map_label_regions: data.labels[${i}] must be an object`, {
        field: `data.labels[${i}]`,
        questionId: qId,
        familyId: fId
      }));
      continue;
    }
    const labelId = entry.id;
    const correctRegionId = entry.correctRegionId;
    if (typeof labelId !== 'string' || labelId.trim() === '') {
      issues.push(blocker('type', STAGE, 'TYPE_MAP_LABEL_MISSING_ID', `map_label_regions: data.labels[${i}].id must be a non-empty string`, {
        field: `data.labels[${i}].id`,
        questionId: qId,
        familyId: fId
      }));
    } else if (seenIds.has(labelId)) {
      issues.push(blocker('type', STAGE, 'TYPE_MAP_LABEL_DUPLICATE_ID', `map_label_regions: data.labels[${i}].id "${labelId}" is duplicated`, {
        field: `data.labels[${i}].id`,
        actual: labelId,
        questionId: qId,
        familyId: fId
      }));
    } else {
      seenIds.add(labelId);
    }
    if (typeof correctRegionId !== 'string' || correctRegionId.trim() === '') {
      issues.push(blocker('type', STAGE, 'TYPE_MAP_LABEL_MISSING_REGION', `map_label_regions: data.labels[${i}].correctRegionId must be a non-empty string`, {
        field: `data.labels[${i}].correctRegionId`,
        questionId: qId,
        familyId: fId
      }));
    }
  }
  return issues;
}

// ─── map_place_cities ────────────────────────────────────────────────────────

export function validateMapPlaceCities(data: Record<string, unknown>, qId: string, fId: string): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const targets = data.targetPoints;
  if (!Array.isArray(targets)) {
    issues.push(blocker('type', STAGE, 'TYPE_MAP_INVALID_TARGET_POINTS', 'map_place_cities: data.targetPoints must be an array of { name?, cityId?, lat, lon }', {
      field: 'data.targetPoints',
      actual: JSON.stringify(targets),
      questionId: qId,
      familyId: fId
    }));
    return issues;
  }
  if (targets.length < 2) {
    issues.push(blocker('type', STAGE, 'TYPE_MAP_TARGET_POINTS_TOO_FEW', `map_place_cities: needs at least 2 targetPoints (got ${targets.length}); use map_locate_point for single-pin questions`, {
      field: 'data.targetPoints',
      actual: String(targets.length),
      expected: '≥ 2',
      questionId: qId,
      familyId: fId
    }));
  }
  for (let i = 0; i < targets.length; i++) {
    const t = targets[i] as Record<string, unknown> | undefined;
    if (!isValidLatLon(t)) {
      issues.push(blocker('type', STAGE, 'TYPE_MAP_TARGET_POINT_INVALID', `map_place_cities: data.targetPoints[${i}] must have finite numeric lat AND lon`, {
        field: `data.targetPoints[${i}]`,
        actual: JSON.stringify(t),
        questionId: qId,
        familyId: fId
      }));
      continue;
    }
    const hasIdentity = typeof (t as Record<string, unknown>).name === 'string' && ((t as Record<string, unknown>).name as string).trim() !== '' || typeof (t as Record<string, unknown>).cityId === 'string' && ((t as Record<string, unknown>).cityId as string).trim() !== '';
    if (!hasIdentity) {
      issues.push(coach('type', STAGE, 'TYPE_MAP_TARGET_POINT_NO_IDENTITY', `map_place_cities: data.targetPoints[${i}] is missing both "name" and "cityId" — feedback will fall back to "Stad N"`, {
        field: `data.targetPoints[${i}]`,
        questionId: qId,
        familyId: fId,
        suggestion: 'Add a human-readable "name" (e.g. "Amsterdam") so the feedback breakdown shows real city names'
      }));
    }
  }
  const tol = data.toleranceKm;
  if (tol === undefined || !isFiniteNumber(tol) || tol <= 0) {
    issues.push(blocker('type', STAGE, 'TYPE_MAP_INVALID_TOLERANCE', `map_place_cities: data.toleranceKm must be a positive number (got ${String(tol)})`, {
      field: 'data.toleranceKm',
      actual: String(tol),
      expected: '> 0 (typically 25-40 km)',
      questionId: qId,
      familyId: fId
    }));
  }
  return issues;
}